import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getPageTitle } from "../../utils/getPageTitle";
import Button from "../../components/common/button";
import "../../assets/styles/industry.css";
import "../../assets/styles/products.css";
import "../../assets/styles/pagination.css";
import SubIndustryData from "../../components/sub-industryData";
import SearchItem from "../../components/searchItem";
import Pagination from "../../components/common/pagination";
import DeleteModal from "../../components/modal/deleteModal";
import { useDeleteSubIndustry } from "../../hooks/sub-industry/deleteSubIndustry";
import { useSubIndustriesPageQuery } from "../../hooks/sub-industry/sub-industry.all";
import { toast } from "react-hot-toast";

const SubIndustries = () => {
  const [selectedSubIndustry, setSelectedSubIndustry] = useState(null);
  const [mode, setMode] = useState("create");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: pageSize,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: pageData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useSubIndustriesPageQuery({
    page,
    limit: pageSize,
    search: debouncedSearchTerm,
  });

  const { mutate: deleteSubIndustry, isLoading: isDeleting } =
    useDeleteSubIndustry();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!pageData) return;
    setPagination({
      currentPage: page,
      totalPages: pageData.totalPages || 1,
      totalDocs: pageData.total || 0,
      limit: pageSize,
      hasNextPage: (pageData.totalPages || 1) > page,
      hasPrevPage: page > 1,
    });
  }, [pageData, page, pageSize]);

  const currentItems = pageData?.items ?? [];

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handleEdit = (subindustry) => {
    navigate(
      `/sub-industries/edit-subIndustry/${subindustry._id || subindustry.id}`,
      { state: { subindustry } },
    );
  };

  const handleDelete = (subindustry) => {
    setSelectedSubIndustry(subindustry);
    setMode("delete");
    setIsModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedSubIndustry) return;
    const id = selectedSubIndustry._id || selectedSubIndustry.id;

    deleteSubIndustry(id, {
      onSuccess: () => {
        toast.success("Sub Industry deleted successfully!");
        setIsModalOpen(false);
        setSelectedSubIndustry(null);
      },
      onError: (err) => {
        toast.error(
          err?.response?.data?.message || "Failed to delete Sub Industry",
        );
      },
    });
  };

  const listErrorMessage =
    error?.response?.data?.message ||
    error?.message ||
    "Failed to load subindustries";

  return (
    <>
      <div className="industry-page">
        <div className="industry-header">
          <div>
            <h1 className="page-title">{getPageTitle(location.pathname)}</h1>
            <p className="page-subtitle">
              Organize industries and sub-industries
            </p>
          </div>
          <Button onClick={() => navigate("/sub-industries/add-subIndustry")}>
            + Add Sub Industry
          </Button>
        </div>

        <div className="list-toolbar">
          <div className="list-toolbar-row">
            <SearchItem
              data={currentItems}
              searchField="title"
              placeholder="Search by sub industry title..."
              wrapSection={false}
              onResultsChange={({ searchItem }) => setSearchTerm(searchItem)}
            />
            <label className="list-toolbar-page-size">
              <span>Items per page:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
              </select>
            </label>
          </div>
        </div>

        {isError ? (
          <div style={{ padding: "20px", color: "#b91c1c" }}>
            Error loading subindustries: {listErrorMessage}
          </div>
        ) : isLoading ? (
          <div style={{ padding: "20px", color: "#666" }}>
            Loading sub-industries...
          </div>
        ) : (
          <>
            <SubIndustryData
              data={currentItems}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />

            {isFetching && !isLoading && (
              <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                Updating...
              </p>
            )}

            <Pagination
              currentPage={page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      <DeleteModal
        isOpen={isModalOpen && mode === "delete"}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedSubIndustry(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Sub Industry"
        itemName={selectedSubIndustry?.title}
        isLoading={isDeleting}
      />
    </>
  );
};

export default SubIndustries;
